import React from 'react';
import { cn } from '@/lib/utils';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';

interface ValidationScoreCardProps {
  type: 'identity' | 'fraud';
  score: number;
  className?: string;
}

export const ValidationScoreCard: React.FC<ValidationScoreCardProps> = ({ type, score, className }) => {
  const isIdentity = type === 'identity';

  // Determine status and colors based on score
  const getStatusConfig = () => {
    if (isIdentity) {
      // Identity Confidence: higher is better
      if (score >= 71) {
        return {
          label: 'High Confidence',
          color: 'text-blue-700',
          bgGradient: 'from-blue-50 to-blue-100',
          barColor: 'bg-blue-500',
          icon: CheckCircle,
          iconColor: 'text-blue-600',
          dotColor: 'bg-blue-500',
        };
      }
      if (score >= 41) {
        return {
          label: 'Moderate Confidence',
          color: 'text-amber-700',
          bgGradient: 'from-amber-50 to-amber-100',
          barColor: 'bg-amber-500',
          icon: Shield,
          iconColor: 'text-amber-600',
          dotColor: 'bg-amber-500',
        };
      }
      return {
        label: 'Low Confidence',
        color: 'text-orange-700',
        bgGradient: 'from-orange-50 to-orange-100',
        barColor: 'bg-orange-500',
        icon: AlertTriangle,
        iconColor: 'text-orange-600',
        dotColor: 'bg-orange-500',
      };
    } else {
      // Fraud Risk: lower is better
      if (score <= 15) {
        return {
          label: 'Low Risk',
          color: 'text-green-700',
          bgGradient: 'from-green-50 to-green-100',
          barColor: 'bg-green-500',
          icon: CheckCircle,
          iconColor: 'text-green-600',
          dotColor: 'bg-green-500',
        };
      }
      if (score <= 40) {
        return {
          label: 'Moderate Risk',
          color: 'text-amber-700',
          bgGradient: 'from-amber-50 to-amber-100',
          barColor: 'bg-amber-500',
          icon: Shield,
          iconColor: 'text-amber-600',
          dotColor: 'bg-amber-500',
        };
      }
      return {
        label: 'High Risk',
        color: 'text-red-700',
        bgGradient: 'from-red-50 to-red-100',
        barColor: 'bg-red-500',
        icon: AlertTriangle,
        iconColor: 'text-red-600',
        dotColor: 'bg-red-500',
      };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;
  const percentage = Math.min(100, Math.max(0, score));

  return (
    <div className={cn('relative overflow-hidden rounded-lg border border-slate-200 bg-gradient-to-br', config.bgGradient, className)}>
      <div className="p-3">
        {/* Header with icon and title */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Icon className={cn('w-4 h-4', config.iconColor)} />
            <span className="text-[10px] font-medium text-slate-600 uppercase tracking-wide">
              {isIdentity ? 'Identity Confidence' : 'Fraud Risk'}
            </span>
          </div>
        </div>

        {/* Score display */}
        <div className="flex items-end gap-2 mb-2">
          <div className={cn('text-3xl font-bold', config.color)}>
            {score.toFixed(1)}
          </div>
          <div className="flex items-center gap-1 mb-0.5">
            <div className={cn('w-1.5 h-1.5 rounded-full', config.dotColor)} />
            <span className={cn('text-xs font-medium', config.color)}>
              {config.label}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-1000 ease-out', config.barColor)}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Range labels */}
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-slate-500">0</span>
          <span className="text-[10px] text-slate-500">100</span>
        </div>
      </div>
    </div>
  );
};
